import React from 'react';
import Button from '../../UI/Button';

export interface ErrorMessageProps {
  title?: string;
  message: string;
  error?: Error;
  onRetry?: () => void;
  onDismiss?: () => void;
  retryLabel?: string;
  dismissLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  showDetails?: boolean;
}

/**
 * Reusable error message component with retry functionality
 * Displays error information with optional retry and dismiss actions
 */
const ErrorMessage: React.FC<ErrorMessageProps> = ({
  title = 'Error',
  message,
  error,
  onRetry,
  onDismiss,
  retryLabel = 'Try Again',
  dismissLabel = 'Dismiss',
  variant = 'danger',
  showDetails = false
}) => {
  const alertClass = `alert alert-${variant}`;
  const iconClass = variant === 'danger' ? 'fa-exclamation-circle' : 
                   variant === 'warning' ? 'fa-exclamation-triangle' : 
                   'fa-info-circle';

  return (
    <div className={alertClass} role="alert">
      <div className="flex items-start">
        <i className={`fas ${iconClass} me-2 mt-1`}></i>
        <div className="flex-grow">
          <h6 className="alert-heading mb-2">{title}</h6>
          <p className="mb-2">{message}</p>
          
          {showDetails && error && process.env.NODE_ENV === 'development' && (
            <details className="mb-2">
              <summary className="btn btn-outline-secondary btn-sm">
                Show Technical Details
              </summary>
              <div className="mt-2 p-2 bg-gray-100 border rounded">
                <strong>Error:</strong> {error.message}
                {error.stack && (
                  <>
                    <br />
                    <strong>Stack Trace:</strong>
                    <pre className="mt-1 small text-gray-500">
                      {error.stack}
                    </pre>
                  </>
                )}
              </div>
            </details>
          )}

          <div className="flex gap-2 flex-wrap">
            {onRetry && (
              <Button
                size="sm"
                primary={variant === 'danger'}
                secondary={variant !== 'danger'}
                onClick={onRetry}
              >
                <i className="fas fa-redo mr-1"></i>
                {retryLabel}
              </Button>
            )}
            {onDismiss && (
              <Button
                size="sm"
                secondary
                onClick={onDismiss}
              >
                {dismissLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorMessage;